import { useEffect } from 'react';
import { useQuery } from '@apollo/client/react';

import { useApolloError } from 'common/hooks/useApolloError';
import { useFeature } from 'common/hooks/useFeature';
import {
    IFeature,
    IRole,
    CurrentUserDocument,
    OffersSettingsDocument,
} from 'graphql/types';
import { useRouteMatch } from 'route/hooks/useRouteMatch';

/**
 * This hook is used to check if the what the portfolio pages show based on permissions
 */
export const usePortfolioViewPermissions = () => {
    const { showApolloError } = useApolloError();
    const { data: currentUserData } = useQuery(CurrentUserDocument);
    const {
        canUse: canUsePortfolio,
        userCanSeeUpgrade,
        loading: loadingPortfolioFeature,
    } = useFeature(IFeature.Portfolio);
    const {
        canUse: canUseOffers,
        loading: loadingOffersFeature,
        userCanSeeUpgrade: userCanSeeOffersUpgrade,
    } = useFeature(IFeature.Offer);
    const { canUse: canUseSkills, loading: loadingSkillsFeature } = useFeature(
        IFeature.Skill
    );
    const {
        showOffers: isOffersPublished,
        showSkills: isSkillsPublished,
        loading: loadingSettings,
    } = useFeature(undefined, true);

    const isTrainingOffersPage = !!useRouteMatch('PORTFOLIO_OFFERS');
    const isTrainingOffersUserPage = !!useRouteMatch('USER_OFFERS');
    const isMeetingsPage = !!useRouteMatch('PORTFOLIO_MEETINGS');
    const isMeetingsUserPage = !!useRouteMatch('USER_MEETINGS');
    const isPortfolioSkillsPage = !!useRouteMatch('PORTFOLIO_SKILLS');
    const isSkillsUserPage = !!useRouteMatch('USER_PORTFOLIO_SKILLS');

    // The portfolio offers page which shows training and courses
    const isOffersPage = isTrainingOffersPage || isTrainingOffersUserPage;
    // The portfolio meetings page which shows meetings and webinars
    const isPortfolioMeetingsPage = isMeetingsPage || isMeetingsUserPage;

    const { roles } = currentUserData?.currentUser || {};
    const currentUserIsManager = roles?.includes(IRole.Manager);

    // Every offers related portfolio page
    const isPortfolioOffersPage = isOffersPage || isPortfolioMeetingsPage;

    const {
        data: offersSettingsData,
        loading: offersSettingsLoading,
        error: offersSettingsError,
    } = useQuery(OffersSettingsDocument);

    useEffect(() => {
        if (offersSettingsError) {
            showApolloError(offersSettingsError);
        }
    }, [offersSettingsError, showApolloError]);

    const loading =
        loadingPortfolioFeature ||
        loadingOffersFeature ||
        offersSettingsLoading ||
        loadingSkillsFeature ||
        loadingSettings;

    const canUsePortfolioOffers = !!(
        !loadingOffersFeature &&
        !loadingSettings &&
        canUseOffers &&
        isOffersPublished
    );

    const canUsePortfolioSkills = !!(
        !loadingSkillsFeature &&
        !loadingSettings &&
        canUseSkills &&
        isSkillsPublished
    );

    const { publishTraining, publishElearning, publishMeetingWebinar } =
        offersSettingsData?.offersSettings || {};

    const hideOffersSection =
        !currentUserIsManager &&
        ((!publishTraining && !publishElearning) || !isOffersPublished);
    const hideMeetingsSection =
        !currentUserIsManager && (!publishMeetingWebinar || !isOffersPublished);

    const portfolioPageNotFound = !canUsePortfolio && !userCanSeeUpgrade;

    // Every offers related portfolio page
    const portfolioOffersPageNotFound =
        isPortfolioOffersPage &&
        !canUsePortfolioOffers &&
        !userCanSeeOffersUpgrade;

    // Skills page not found
    const portfolioSkillsPageNotFound =
        (isPortfolioSkillsPage || isSkillsUserPage) && !canUsePortfolioSkills;

    // The portfolio offers page which shows training and courses
    const offersPageNotFound = isOffersPage && hideOffersSection;
    // The portfolio meetings page which shows meetings and webinars
    const meetingsPageNotFound = isPortfolioMeetingsPage && hideMeetingsSection;

    const pageNotFound =
        !loading &&
        (portfolioPageNotFound ||
            portfolioOffersPageNotFound ||
            offersPageNotFound ||
            meetingsPageNotFound ||
            portfolioSkillsPageNotFound);

    const isPortfolioUpgradePage = !canUsePortfolio && userCanSeeUpgrade;

    const showUpgradePage =
        isPortfolioUpgradePage ||
        (isPortfolioOffersPage &&
            !canUsePortfolioOffers &&
            userCanSeeOffersUpgrade);

    return {
        canUsePortfolioOffers,
        userCanSeeUpgrade,
        loading,
        showUpgradePage,
        isPortfolioUpgradePage,
        pageNotFound,
        isOffersPublished,
        hideOffersSection,
        hideMeetingsSection,
        canUseSkills: canUsePortfolioSkills,
    };
};
